<script>
import axios from '@/utils/axios';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

export default {
  inject: ['setLoading', 'auth'],
  components: { FullCalendar },
  data() {
    return {
      loading: true,
      records: [],
      selected: null,
      curYear: new Date().getFullYear(),
      curMonth: new Date().getMonth() + 1,
      isMobile: window.innerWidth < 576,
      calendarOptions: {
        plugins: [dayGridPlugin, listPlugin, interactionPlugin],
        initialView: window.innerWidth < 576 ? 'listMonth' : 'dayGridMonth',
        headerToolbar: { left: 'prev,next today', center: 'title', right: '' },
        height: 'auto',
        firstDay: 0,
        fixedWeekCount: false,
        noEventsText: 'No attendance records for this month',
        listDayFormat: { weekday: 'short', month: 'short', day: 'numeric' },
        listDaySideFormat: false,
        events: [],
        eventClick: this.onEventClick,
        datesSet: this.onDatesSet,
      },
    };
  },
  computed: {
    summary() {
      const s = { present: 0, absent: 0, late: 0, half_day: 0, on_leave: 0, holiday: 0, hours: 0 };
      for (const r of this.records) {
        if (s[r.status] !== undefined) s[r.status]++;
        s.hours += parseFloat(r.working_hours) || 0;
      }
      s.hours = Math.round(s.hours * 100) / 100;
      return s;
    },
  },
  methods: {
    dateKey(d) {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    },
    fmtTime(val) {
      if (!val) return null;
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
    },
    statusLabel(s) {
      const map = { present: 'Present', absent: 'Absent', half_day: 'Half Day', late: 'Late', on_leave: 'Leave', holiday: 'Holiday', weekend: 'Week off' };
      return map[s] || s;
    },
    statusShort(s) {
      const map = { present: 'P', absent: 'A', half_day: 'HD', late: 'Late', on_leave: 'Leave', holiday: 'Hol', weekend: 'Off' };
      return map[s] || s;
    },
    showBand(s) { return s && s !== 'present'; },
    mapLink(lat, lng) { return (lat && lng) ? `https://www.google.com/maps?q=${lat},${lng}` : null; },

    onDatesSet(info) {
      const d = info.view.currentStart;
      this.curYear = d.getFullYear();
      this.curMonth = d.getMonth() + 1;
      this.fetchData();
    },

    async fetchData() {
      this.loading = true;
      try {
        const res = await axios.get('/hrms/attendances/my', { params: { month: this.curMonth, year: this.curYear } });
        this.records = res.data.data || [];
      } catch (error) {
        this.$swalToast(error.message, 'error');
        this.records = [];
      } finally {
        this.buildEvents();
        this.loading = false;
      }
    },

    buildEvents() {
      const events = [];
      const recDates = new Set(this.records.map(r => this.dateKey(r.date)));

      // attendance + holiday records
      for (const r of this.records) {
        events.push({
          start: this.dateKey(r.date), allDay: true,
          title: this.statusLabel(r.status),
          classNames: [`ev-${r.status}`],
          extendedProps: { rec: r, type: 'att' },
        });
      }

      // week-off (Sat/Sun without a record) — skip in list view to avoid clutter
      if (!this.isMobile) {
        const daysInMonth = new Date(this.curYear, this.curMonth, 0).getDate();
        for (let d = 1; d <= daysInMonth; d++) {
          const dObj = new Date(this.curYear, this.curMonth - 1, d);
          const dow = dObj.getDay();
          const key = this.dateKey(dObj);
          if (dow === 0 && !recDates.has(key)) {
            events.push({ start: key, allDay: true, classNames: ['ev-weekend'], extendedProps: { type: 'weekoff' } });
          }
        }
      }
      this.calendarOptions.events = events;
    },

    onEventClick(info) {
      if (info.event.extendedProps.type !== 'att') return;
      this.selected = info.event.extendedProps.rec;
      this.$openModal('my_att_detail_modal');
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between gap-3 mb-4">
        <div class="d-flex align-items-center gap-3">
          <div class="ma-head-ico"><i class="ti ti-calendar-user"></i></div>
          <div>
            <h4 class="mb-0 fw-bold" style="color:#0f172a;">My Attendance</h4>
            <p class="mb-0" style="font-size:12.5px; color:#94a3b8;">Your monthly attendance overview</p>
          </div>
        </div>
        <router-link to="/hrms/attendance-regularizations" class="ma-reg-btn">
          <i class="ti ti-clock-edit"></i> Regularizations
        </router-link>
      </div>

      <!-- Summary -->
      <div class="ma-stats mb-3">
        <div class="ma-stat"><div class="ma-stat-ico" style="background:#dcfce7;color:#16a34a;"><i class="ti ti-checks"></i></div><div><div class="ma-stat-num">{{ summary.present }}</div><div class="ma-stat-lbl">Present</div></div></div>
        <div class="ma-stat"><div class="ma-stat-ico" style="background:#ffedd5;color:#ea580c;"><i class="ti ti-clock-exclamation"></i></div><div><div class="ma-stat-num">{{ summary.late }}</div><div class="ma-stat-lbl">Late</div></div></div>
        <div class="ma-stat"><div class="ma-stat-ico" style="background:#fee2e2;color:#dc2626;"><i class="ti ti-x"></i></div><div><div class="ma-stat-num">{{ summary.absent }}</div><div class="ma-stat-lbl">Absent</div></div></div>
        <div class="ma-stat"><div class="ma-stat-ico" style="background:#ffedd5;color:#ea580c;"><i class="ti ti-beach"></i></div><div><div class="ma-stat-num">{{ summary.on_leave }}</div><div class="ma-stat-lbl">Leave</div></div></div>
        <div class="ma-stat"><div class="ma-stat-ico" style="background:#eff6ff;color:#0891b2;"><i class="ti ti-hourglass"></i></div><div><div class="ma-stat-num">{{ summary.hours }}<small>h</small></div><div class="ma-stat-lbl">Total Hours</div></div></div>
      </div>

      <!-- Calendar -->
      <div class="ma-card">
        <FullCalendar :options="calendarOptions">
          <template #eventContent="arg">
            <!-- List (agenda) view — mobile -->
            <div v-if="arg.view.type === 'listMonth'" class="ev-list-row" :class="'ev-list-' + (arg.event.extendedProps.rec?.status || 'default')">
              <div class="ev-list-status" :class="'ev-band-' + arg.event.extendedProps.rec?.status">
                {{ statusLabel(arg.event.extendedProps.rec?.status) }}
              </div>
              <div class="ev-list-detail" v-if="arg.event.extendedProps.rec?.status !== 'holiday'">
                <span v-if="fmtTime(arg.event.extendedProps.rec?.punch_in)"><i class="ti ti-login-2"></i>{{ fmtTime(arg.event.extendedProps.rec.punch_in) }}</span>
                <span v-if="fmtTime(arg.event.extendedProps.rec?.punch_out)"><i class="ti ti-logout-2"></i>{{ fmtTime(arg.event.extendedProps.rec.punch_out) }}</span>
                <span v-if="arg.event.extendedProps.rec?.working_hours"><i class="ti ti-clock"></i>{{ arg.event.extendedProps.rec.working_hours }} hrs</span>
              </div>
            </div>

            <!-- Grid view — desktop -->
            <template v-else>
              <!-- Week off -->
              <div v-if="arg.event.extendedProps.type === 'weekoff'" class="ev-band ev-band-weekend">
                <span class="ev-band-full">Week off</span>
                <span class="ev-band-short">Off</span>
              </div>
              <!-- Attendance / Holiday -->
              <template v-else>
                <div class="ev-lines" v-if="arg.event.extendedProps.rec.status !== 'holiday'">
                  <div class="ev-line-in" v-if="fmtTime(arg.event.extendedProps.rec.punch_in)"><i class="ti ti-login-2"></i>{{ fmtTime(arg.event.extendedProps.rec.punch_in) }}</div>
                  <div class="ev-line-out" v-if="fmtTime(arg.event.extendedProps.rec.punch_out)"><i class="ti ti-logout-2"></i>{{ fmtTime(arg.event.extendedProps.rec.punch_out) }}</div>
                  <div class="ev-line-hours" v-if="arg.event.extendedProps.rec.working_hours"><i class="ti ti-clock"></i>{{ arg.event.extendedProps.rec.working_hours }} hrs</div>
                </div>
                <div v-if="showBand(arg.event.extendedProps.rec.status)" class="ev-band" :class="'ev-band-' + arg.event.extendedProps.rec.status">
                  <span class="ev-band-full">{{ statusLabel(arg.event.extendedProps.rec.status) }}</span>
                  <span class="ev-band-short">{{ statusShort(arg.event.extendedProps.rec.status) }}</span>
                </div>
              </template>
            </template>
          </template>
        </FullCalendar>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- Detail modal -->
  <div class="modal fade" id="my_att_detail_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content" v-if="selected">
        <div class="modal-header">
          <h4 class="modal-title">{{ $formatDate(selected.date) }} — {{ statusLabel(selected.status) }}</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal"><i class="ti ti-x"></i></button>
        </div>
        <div class="modal-body">
          <div class="ma-d-row"><span class="ma-d-lbl">Punch In</span><span class="ma-d-val">{{ fmtTime(selected.punch_in) || '—' }}<a v-if="mapLink(selected.punch_in_latitude, selected.punch_in_longitude)" :href="mapLink(selected.punch_in_latitude, selected.punch_in_longitude)" target="_blank" class="ma-d-map"><i class="ti ti-map-pin"></i></a></span></div>
          <div class="ma-d-row"><span class="ma-d-lbl">Punch Out</span><span class="ma-d-val">{{ fmtTime(selected.punch_out) || '—' }}<a v-if="mapLink(selected.punch_out_latitude, selected.punch_out_longitude)" :href="mapLink(selected.punch_out_latitude, selected.punch_out_longitude)" target="_blank" class="ma-d-map"><i class="ti ti-map-pin"></i></a></span></div>
          <div class="ma-d-row"><span class="ma-d-lbl">Working Hours</span><span class="ma-d-val">{{ selected.working_hours ? selected.working_hours + 'h' : '—' }}</span></div>
          <div class="ma-d-row"><span class="ma-d-lbl">Status</span><span class="ma-d-val">{{ statusLabel(selected.status) }}</span></div>
          <div class="ma-d-row"><span class="ma-d-lbl">Source</span><span class="ma-d-val">{{ selected.source || '—' }}</span></div>
          <div class="ma-d-row"><span class="ma-d-lbl">Remarks</span><span class="ma-d-val">{{ selected.remarks || '—' }}</span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ma-head-ico { width:46px;height:46px;border-radius:13px;background:linear-gradient(135deg,#4f46e5,#7c3aed);display:flex;align-items:center;justify-content:center;color:#fff;font-size:22px;box-shadow:0 8px 18px -6px rgba(79,70,229,.5); }
.ma-reg-btn { display:inline-flex;align-items:center;gap:7px;font-size:13px;font-weight:600;text-decoration:none;padding:9px 16px;border-radius:10px;background:#fff;color:#475569;border:1px solid #e2e8f0;transition:all .15s ease;white-space:nowrap; }
.ma-reg-btn:hover { border-color:#cbd5e1;color:#1e293b; }

.ma-stats { display:grid;grid-template-columns:repeat(5,1fr);gap:14px; }
.ma-stat { display:flex;align-items:center;gap:12px;background:#fff;border:1px solid #eef2f7;border-radius:14px;padding:16px 18px;box-shadow:0 1px 3px rgba(0,0,0,.03); }
.ma-stat-ico { width:42px;height:42px;border-radius:11px;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0; }
.ma-stat-num { font-size:22px;font-weight:800;color:#0f172a;line-height:1; }
.ma-stat-num small { font-size:13px;font-weight:600;color:#94a3b8; }
.ma-stat-lbl { font-size:11px;color:#94a3b8;font-weight:500;margin-top:3px; }

.ma-card { background:#fff;border:1px solid #eef2f7;border-radius:16px;padding:18px 20px;box-shadow:0 1px 3px rgba(0,0,0,.03); }

/* event cell content (grid view) */
.ev-lines { display:flex; flex-direction:column; gap:2px; padding:3px 5px 1px; }
.ev-lines > div { display:flex; align-items:center; gap:5px; font-size:10.5px; font-weight:600; color:#334155; line-height:1.35; }
.ev-lines > div i { font-size:12px; width:13px; text-align:center; }
.ev-lines > div:nth-child(1) i { color:#16a34a; }
.ev-lines > div:nth-child(2) i { color:#ef4444; }
.ev-lines > div:nth-child(3) i { color:#6366f1; }

.ev-band {
  text-align:center; font-size:9.5px; font-weight:700; letter-spacing:.4px;
  padding:3px 6px; border-radius:6px; margin:3px 4px 0; text-transform:uppercase;
}
.ev-band-absent   { background:#fef2f2; color:#dc2626; }
.ev-band-half_day { background:#fffbeb; color:#b45309; }
.ev-band-late     { background:#fff7ed; color:#ea580c; }
.ev-band-on_leave { background:#fff1e6; color:#c2410c; }
.ev-band-holiday  { background:#fdf2f8; color:#db2777; }
.ev-band-weekend  { background:#f1f5f9; color:#64748b; letter-spacing:.2px; text-transform:none; font-weight:600; }
.ev-band-present  { background:#dcfce7; color:#16a34a; }

/* toggle full/short band labels — desktop shows full, mobile shows short */
.ev-band-short { display:none; }

/* list view rows */
.ev-list-row {
  display:flex; align-items:center; justify-content:space-between; gap:10px;
  padding:12px 14px; width:100%;
}
.ev-list-status {
  font-size:11px; font-weight:700; padding:4px 10px; border-radius:20px; flex-shrink:0;
}
.ev-list-detail { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
.ev-list-detail span { display:inline-flex; align-items:center; gap:4px; font-size:11.5px; font-weight:600; color:#475569; }
.ev-list-detail span:nth-child(1) i { color:#16a34a; }
.ev-list-detail span:nth-child(2) i { color:#ef4444; }
.ev-list-detail span:nth-child(3) i { color:#6366f1; }

/* ---- FullCalendar professional polish ---- */
:deep(.fc) { --fc-border-color:#f1f5f9; }
:deep(.fc-theme-standard .fc-scrollgrid) { border:1px solid #eef2f7; border-radius:14px; overflow:hidden; }
:deep(.fc-theme-standard td), :deep(.fc-theme-standard th) { border-color:#f1f5f9; }

/* toolbar */
:deep(.fc .fc-toolbar.fc-header-toolbar) { margin-bottom:18px; }
:deep(.fc .fc-toolbar-title) { font-size:17px; font-weight:700; color:#0f172a; letter-spacing:-.2px; }
:deep(.fc .fc-button) {
  background:#fff; border:1px solid #e2e8f0; color:#475569;
  text-transform:capitalize; font-size:12.5px; font-weight:600;
  padding:6px 12px; box-shadow:none; border-radius:9px;
}
:deep(.fc .fc-button:hover) { background:#f8fafc; border-color:#cbd5e1; color:#1e293b; }
:deep(.fc .fc-button:focus) { box-shadow:none; }
:deep(.fc .fc-button-primary:not(:disabled).fc-button-active),
:deep(.fc .fc-button-primary:not(:disabled):active) { background:#4f46e5; border-color:#4f46e5; color:#fff; }
:deep(.fc .fc-prev-button), :deep(.fc .fc-next-button) { padding:6px 9px; }

/* weekday header */
:deep(.fc .fc-col-header-cell) { background:#fafbfc; padding:11px 0; }
:deep(.fc .fc-col-header-cell-cushion) {
  font-size:11px; font-weight:700; color:#94a3b8;
  text-transform:uppercase; letter-spacing:.6px;
}

/* day cells */
:deep(.fc .fc-daygrid-day-frame) { min-height:104px; padding:2px; }
:deep(.fc .fc-daygrid-day) { transition:background .12s ease; }
:deep(.fc .fc-daygrid-day:hover) { background:#fafbfc; }
:deep(.fc .fc-daygrid-day-number) { font-size:12px; font-weight:600; color:#64748b; padding:7px 9px; }
:deep(.fc .fc-day-other .fc-daygrid-day-number) { opacity:.3; }

/* today */
:deep(.fc .fc-daygrid-day.fc-day-today) { background:#f5f7ff; }
:deep(.fc .fc-day-today .fc-daygrid-day-number) {
  color:#fff; background:#4f46e5; font-weight:700;
  border-radius:50%; width:24px; height:24px;
  display:inline-flex; align-items:center; justify-content:center;
  padding:0; margin:5px 6px;
}

/* events (grid) */
:deep(.fc-event) { background:transparent !important; border:none !important; box-shadow:none !important; cursor:pointer; }
:deep(.fc-daygrid-day-events) { padding:0 3px 5px; margin-top:0; }
:deep(.fc-daygrid-event-harness) { margin-top:1px !important; }
:deep(.fc-daygrid-event:hover) { filter:brightness(.97); }

/* ---- List (agenda) view — clean rows, used on mobile ---- */
:deep(.fc-list) { border:none !important; }
:deep(.fc-list-day-cushion) { background:#fafbfc !important; padding:9px 12px !important; }
:deep(.fc-list-day-text), :deep(.fc-list-day-side-text) { font-size:12.5px; font-weight:700; color:#0f172a; }
:deep(.fc-list-event) { cursor:pointer; }
:deep(.fc-list-event:hover td) { background:#fafbfc !important; }
:deep(.fc-list-event td) { border-color:#f1f5f9 !important; padding:0 !important; }
:deep(.fc-list-event-dot) { display:none; }
:deep(.fc-list-event-graphic) { display:none; }
:deep(.fc-list-empty) { padding:40px 20px; font-size:13px; color:#94a3b8; background:#fff; }

/* detail modal */
.ma-d-row { display:flex;justify-content:space-between;align-items:center;padding:9px 0;border-bottom:1px solid #f1f5f9; }
.ma-d-row:last-child { border-bottom:none; }
.ma-d-lbl { font-size:12px;color:#94a3b8;font-weight:600; }
.ma-d-val { font-size:13px;color:#1e293b;font-weight:600;display:inline-flex;align-items:center;gap:6px; }
.ma-d-map { color:#4f46e5; }

@media (max-width:992px){ .ma-stats { grid-template-columns:repeat(2,1fr); } }

@media (max-width:576px){
  /* toolbar */
  :deep(.fc .fc-toolbar.fc-header-toolbar){ flex-direction:column; gap:8px; align-items:stretch; margin-bottom:12px; }
  :deep(.fc .fc-toolbar-chunk){ display:flex; justify-content:center; }
  :deep(.fc .fc-toolbar-title){ font-size:14px; }
  :deep(.fc .fc-button){ padding:4px 9px; font-size:11px; }

  .ma-card{ padding:10px 6px; }

  /* stats: horizontal scroll instead of cramped 2-col grid */
  .ma-stats { display:flex; overflow-x:auto; gap:8px; padding-bottom:4px; -webkit-overflow-scrolling:touch; grid-template-columns:none; }
  .ma-stat { flex:0 0 auto; min-width:108px; padding:10px 12px; }
  .ma-stat-num { font-size:17px; }
  .ma-stat-lbl { font-size:10px; }
}
</style>